import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { ImageIcon, Star, Download } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { assetDetailStyles } from "@/features/assets/services/assetDetailStyles";
import { getPhotoTag } from "./getPhotoTag";
import type { PhotoWithUrl } from "./types";

interface PhotoGridProps {
  photosWithUrls: PhotoWithUrl[];
  loading: boolean;
  onSelectPhoto: (index: number) => void;
  onSetPrimary: (photoId: string) => void;
  onDownload: (photo: PhotoWithUrl) => void;
}

export function PhotoGrid({
  photosWithUrls,
  loading,
  onSelectPhoto,
  onSetPrimary,
  onDownload,
}: PhotoGridProps) {
  return (
    <Card className={cn(assetDetailStyles.premiumCard, "relative overflow-hidden")}>
      <div className={assetDetailStyles.decorativeOverlay} />
      <CardHeader className="relative z-10">
        <CardTitle className={cn("flex items-center gap-2", assetDetailStyles.gradientHeader)}>
          <ImageIcon className="h-5 w-5" />
          Photo Gallery ({photosWithUrls.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="relative z-10">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading photos...</p>
          </div>
        ) : photosWithUrls.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photosWithUrls.map((photo, index) => (
              <div
                key={photo.id}
                className="relative group rounded-lg overflow-hidden border border-border/40 bg-card/50 hover:border-primary/40 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.2)] cursor-pointer"
                onClick={() => onSelectPhoto(index)}
              >
                {/* Photo tag badge */}
                {getPhotoTag(photo) && (
                  <Badge className="absolute top-2 left-2 z-10 bg-primary/90 text-primary-foreground backdrop-blur-sm">
                    {getPhotoTag(photo)}
                  </Badge>
                )}
                <div className="aspect-square">
                  {photo.signedUrl ? (
                    <img
                      src={photo.signedUrl}
                      alt={photo.document_name}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full bg-muted/20">
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Overlay with actions - pointer-events-none allows click-through */}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetPrimary(photo.id);
                    }}
                    title="Set as primary"
                    className="bg-primary/90 hover:bg-primary pointer-events-auto"
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(photo);
                    }}
                    title="Download"
                    className="bg-primary/90 hover:bg-primary pointer-events-auto"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>

                {/* Photo info - also pointer-events-none to allow click-through */}
                <div className="p-2 bg-background/95 backdrop-blur-sm border-t border-border/40 pointer-events-none">
                  <p className="text-xs font-medium truncate text-foreground">{photo.document_name}</p>
                  <p className="text-xs text-muted-foreground">{photo.category}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No photos uploaded yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Upload photos using the form above
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
